import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import { SUPPORTED_LANGUAGES, getLanguageInfo } from '@/i18n';
import { LanguageModal } from './LanguageModal';
import { Flag } from './Flag';

// Thin wrapper that keeps the existing LanguageSelector call sites working
// (Dashboard Header, Guest Portal Header) while routing all language picking
// through the new modal. The button still adapts to `variant` / `darkMode`
// so the surrounding chrome looks the same as before — only the picker UI
// is now a slide-up sheet instead of an inline dropdown.

interface LanguageSelectorProps {
  /** Visual style of the trigger button — does NOT affect the modal itself. */
  variant?: 'default' | 'compact' | 'guest';
  showFlag?: boolean;
  /** Kept for backwards compat; the modal handles its own theming. */
  showSearch?: boolean;
  darkMode?: boolean;
  /** Called after the language changed (e.g. to save it to the profile). */
  onLanguageChanged?: (code: string) => void;
}

export function LanguageSelector({
  variant = 'default',
  showFlag = true,
  darkMode = false,
  onLanguageChanged,
}: LanguageSelectorProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const currentLang = getLanguageInfo(i18n.language) || SUPPORTED_LANGUAGES[0];

  const baseButton =
    'relative flex items-center gap-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

  const buttonClass = (() => {
    if (variant === 'guest') {
      return `${baseButton} h-9 px-3 ${
        darkMode
          ? 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white focus:ring-slate-500'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900 focus:ring-gray-400'
      }`;
    }
    if (variant === 'compact') {
      return `${baseButton} h-9 w-9 justify-center hover:bg-[hsl(var(--muted))] focus:ring-[hsl(var(--primary))]`;
    }
    return `${baseButton} h-10 px-4 border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] focus:ring-[hsl(var(--primary))]`;
  })();

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={buttonClass}
        aria-label={t('common.selectLanguage')}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {variant === 'compact' ? (
          <>
            <Globe className="w-[18px] h-[18px]" />
            <span
              className={`absolute -bottom-0.5 -right-0.5 text-[9px] font-bold uppercase ${
                darkMode ? 'text-slate-400' : 'text-[hsl(var(--muted-foreground))]'
              }`}
            >
              {currentLang.code}
            </span>
          </>
        ) : (
          <>
            {showFlag && <Flag languageCode={currentLang.code} size="md" />}
            <span className="font-medium text-sm">{currentLang.nativeName}</span>
            <ChevronDown className="w-4 h-4 opacity-70" />
          </>
        )}
      </button>

      <LanguageModal
        open={open}
        onClose={() => setOpen(false)}
        scope="global"
        onLanguageChanged={onLanguageChanged}
      />
    </>
  );
}

export default LanguageSelector;
